import type { FC } from 'react';
import { useCallback, useMemo } from 'react';

import { useGameConfig } from '../../../../config/GameConfigProvider';
import { useCashierCallbackConnected } from '../../../../hooks/useCashierCallbackConnected';
import { useExitCallbackConnected } from '../../../../hooks/useExitCallbackConnected';
import { useIsLobbyButtonHiddenConnected } from '../../../../hooks/useIsLobbyButtonHiddenConnected';
import { useShouldStayConnected } from '../../../../hooks/useShouldStayConnected';
import { Logger } from '../../../../logger/Logger';
import { useCoreDispatch } from '../../../../store/CoreStore';
import { useChatSlice } from '../../../../store/slices/chat/selectors';
import { useConfigSlice } from '../../../../store/slices/config/selectors';
import { useHeaderSlice } from '../../../../store/slices/header/selectors';
import { headerActions } from '../../../../store/slices/header/slice';
import { popupActions } from '../../../../store/slices/popup/slice';
import { redirectParentIframe } from '../../../../utils/redirect';
import { PopupType } from '../../../popup/types';
import { NavPages } from '../../navPages/NavPagesConnected';
import type { ClickCallback, NavDrawerHomeSlideButtonCallbacks, NavDrawerHomeSlideLayoutConfig } from '../types';

import { NavDrawerHomeSlide } from './NavDrawerHomeSlide';

const logHomeButtonClickEvent = (eventName: string): void => Logger.logEvent(eventName);

export const NavDrawerHomeSlideConnected: FC = () => {
  const isNavDrawerOpen = useHeaderSlice.getNavOpen();
  const dispatch = useCoreDispatch();
  const cashierCallback = useCashierCallbackConnected();
  const exitCallback = useExitCallbackConnected();
  const isLobbyButtonHidden = useIsLobbyButtonHiddenConnected();
  const shouldStayAtTable = useShouldStayConnected();
  const { quickGuideContent } = useGameConfig();

  const exitUrl = useConfigSlice.casinoConfig()?.exitUrl;
  const isChatDisabled = useConfigSlice.isChatDisabled();
  const isLiveSupportRead = useChatSlice.getIsLiveSupportRead();
  const isLobbyOpen = useHeaderSlice.getIsMiniLobbyOpen();
  const disabledJurisdictionChat = !!useConfigSlice.jurisdiction()?.disableChat;

  const layoutConfig: NavDrawerHomeSlideLayoutConfig = useMemo(() => {
    return {
      lobby: isLobbyButtonHidden,
      cashier: Boolean(cashierCallback),
      chat: isChatDisabled || disabledJurisdictionChat,
      quickGuide: Boolean(quickGuideContent),
      liveSupport: !isChatDisabled,
      home: Boolean(exitUrl),
    };
  }, [cashierCallback, disabledJurisdictionChat, exitUrl, isChatDisabled, isLobbyButtonHidden, quickGuideContent]);

  const lobbyClick: ClickCallback<HTMLElement> = useCallback(
    (event): void => {
      event?.preventDefault();
      event?.stopPropagation();

      if (!isLobbyOpen) {
        logHomeButtonClickEvent('openLobbyMobMenu');
        dispatch(
          shouldStayAtTable
            ? popupActions.pushPopup(PopupType.DontLeaveRoundInProgress)
            : headerActions.setMiniLobbyOpen(true)
        );
      }
    },
    [shouldStayAtTable, dispatch, isLobbyOpen]
  );

  const chatClick = useCallback(() => {
    logHomeButtonClickEvent('openChatMobMenu');
    dispatch(headerActions.pushNavRoute(NavPages.CHAT));
  }, []);

  const historyClick = useCallback(() => {
    logHomeButtonClickEvent('openHistoryMobMenu');
    dispatch(headerActions.pushNavRoute(NavPages.HISTORY));
  }, []);

  const quickGuideClick = useCallback(() => {
    logHomeButtonClickEvent('openQuickGuideMobMenu');
    dispatch(headerActions.pushNavRoute(NavPages.QUICK_GUIDE));
  }, []);

  const betLimitsClick = useCallback(() => {
    logHomeButtonClickEvent('openBetLimitsMobMenu');
    dispatch(headerActions.pushNavRoute(NavPages.BET_LIMITS));
  }, []);

  const settingsClick = useCallback(() => {
    logHomeButtonClickEvent('openSettingsMobMenu');
    dispatch(headerActions.pushNavRoute(NavPages.SETTINGS));
  }, []);

  const helpClick = useCallback(() => {
    logHomeButtonClickEvent('openHelpMobMenu');
    dispatch(headerActions.pushNavRoute(NavPages.HELP));
  }, []);

  const liveSupportClick = useCallback(() => {
    logHomeButtonClickEvent('openSupportChatMobMenu');
    dispatch(headerActions.pushNavRoute(NavPages.SUPPORT));
  }, []);

  const homeClick = useCallback(() => {
    logHomeButtonClickEvent('openHomeMobMenu');
    if (shouldStayAtTable) {
      dispatch(popupActions.pushPopup(PopupType.DontLeaveRoundInProgress));
      return;
    }
    exitCallback();
    if (exitUrl) redirectParentIframe(exitUrl);
  }, [exitCallback, exitUrl, shouldStayAtTable]);

  const buttonCallbacks: NavDrawerHomeSlideButtonCallbacks = useMemo(() => {
    return {
      lobbyClick,
      cashierClick: cashierCallback,
      chatClick,
      betLimitsClick,
      helpClick,
      historyClick,
      settingsClick,
      homeClick,
      liveSupportClick,
      quickGuideClick,
    };
  }, [
    betLimitsClick,
    cashierCallback,
    chatClick,
    helpClick,
    historyClick,
    homeClick,
    liveSupportClick,
    lobbyClick,
    settingsClick,
    quickGuideClick,
  ]);
  return (
    <NavDrawerHomeSlide
      layoutConfig={layoutConfig}
      buttonCallbacks={buttonCallbacks}
      isNavDrawerOpen={isNavDrawerOpen}
      showLiveSupportReadBadge={!isLiveSupportRead}
    />
  );
};
